import { getBluetoothIosPrintersConfig } from '@/services/printers/bluetoothIos';
import { getGenericEscposPrintersConfig } from '@/services/printers/genericEscpos';
import { getUsbAndroidPrintersConfig } from '@/services/printers/usbAndroid';
import { getUsbWindowsPrintersConfig } from '@/services/printers/usbWindows';
import { PrinterBackend, PrinterDuty } from '@/services/printers/types';
import {
  supportsBluetoothIosPrinting,
  supportsGenericEscposPrinting,
  supportsUsbAndroidPrinting,
  supportsUsbWindowsPrinting,
} from '@/utils/platformUtils';

/** Backends are checked in this order when resolving a duty. */
export const PRINTER_BACKEND_ORDER: PrinterBackend[] = [
  'usb_windows',
  'usb_android',
  'bluetooth_ios',
  'generic_escpos',
];

export const isBackendAvailableOnDevice = (backend: PrinterBackend): boolean => {
  if (backend === 'generic_escpos') {
    return supportsGenericEscposPrinting();
  }
  if (backend === 'usb_android') {
    return supportsUsbAndroidPrinting();
  }
  if (backend === 'usb_windows') {
    return supportsUsbWindowsPrinting();
  }
  if (backend === 'bluetooth_ios') {
    return supportsBluetoothIosPrinting();
  }
  return false;
};

export const backendHasPrinterForDuty = (
  backend: PrinterBackend,
  duty: PrinterDuty
): boolean => {
  if (!isBackendAvailableOnDevice(backend)) {
    return false;
  }

  if (backend === 'usb_android') {
    const config = getUsbAndroidPrintersConfig();
    return config.enabled && config.printers.some((printer) => printer.duties.includes(duty));
  }

  if (backend === 'usb_windows') {
    const config = getUsbWindowsPrintersConfig();
    return config.enabled && config.printers.some((printer) => printer.duties.includes(duty));
  }

  if (backend === 'bluetooth_ios') {
    const config = getBluetoothIosPrintersConfig();
    return config.enabled && config.printers.some((printer) => printer.duties.includes(duty));
  }

  const config = getGenericEscposPrintersConfig();
  return config.enabled && config.printers.some((printer) => printer.duties.includes(duty));
};

export const resolveBackendForDuty = (duty: PrinterDuty): PrinterBackend | null => {
  for (const backend of PRINTER_BACKEND_ORDER) {
    if (backendHasPrinterForDuty(backend, duty)) {
      return backend;
    }
  }
  return null;
};

export const resolveBackendsForDuty = (duty: PrinterDuty): PrinterBackend[] => {
  const backends: PrinterBackend[] = [];
  for (const backend of PRINTER_BACKEND_ORDER) {
    if (backendHasPrinterForDuty(backend, duty)) {
      backends.push(backend);
    }
  }
  return backends;
};

export const isCashierDutyReady = (): boolean => {
  const backend = resolveBackendForDuty('cashier');
  if (!backend) return false;

  return isBackendAvailableOnDevice(backend);
};
